import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Eye } from "lucide-react";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { KbSectionListRow } from "../../types/gameRuleKbSection";
import { TagBadge } from "../atoms/TagBadge";
import { createDataTablePagination } from "./DataTablePagination";
import { withScrollableDataTable } from "./withScrollableDataTable";

const pagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30, 50],
});
const ScrollableDataTable = withScrollableDataTable(DataTable);

type GameRuleKbSectionsTableProps = {
  readonly rows: readonly KbSectionListRow[];
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number) => void;
  readonly onView: (row: KbSectionListRow) => void;
};

export const GameRuleKbSectionsTable = ({
  rows,
  totalRows,
  isLoading,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  onView,
}: GameRuleKbSectionsTableProps) => {
  const columns: TableColumn<KbSectionListRow>[] = useMemo(
    () => [
      {
        id: "name",
        name: "Section Name",
        selector: (row) => row.name,
        sortable: false,
        grow: 2,
        cell: (row) => (
          <button
            type="button"
            onClick={() => onView(row)}
            className="line-clamp-2 text-left font-medium text-primary-700 hover:underline"
            title={row.name}
          >
            {row.name}
          </button>
        ),
      },
      {
        id: "tag",
        name: "Tag",
        selector: (row) => row.primaryTag ?? "",
        sortable: false,
        width: "200px",
        cell: (row) =>
          row.primaryTag ? (
            <TagBadge label={row.primaryTag} />
          ) : (
            <span className="text-sm text-gray-400">—</span>
          ),
      },
      {
        id: "version",
        name: "Active Version",
        selector: (row) => row.version,
        sortable: false,
        width: "150px",
        cell: (row) => <span className="text-sm tabular-nums text-gray-700">v{row.version}</span>,
      },
      {
        id: "total_versions",
        name: "Total Versions",
        selector: (row) => row.totalVersions,
        sortable: false,
        width: "200px",
        cell: (row) => (
          <span className="text-sm text-gray-700">
            {row.totalVersions} {row.totalVersions === 1 ? "version" : "versions"}
          </span>
        ),
      },
      {
        id: "field_count",
        name: "Fields",
        selector: (row) => row.fieldCount,
        sortable: false,
        width: "100px",
        cell: (row) => <span className="text-sm tabular-nums text-gray-700">{row.fieldCount}</span>,
      },
      {
        id: "actions",
        name: "Action",
        sortable: false,
        width: "120px",
        right: true,
        cell: (row) => (
          <div className="flex items-center justify-end">
            <button
              type="button"
              title="View section detail"
              onClick={() => onView(row)}
              className="inline-flex rounded p-1.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              <Eye className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ),
      },
    ],
    [onView],
  );

  return (
    <div className="min-h-0 w-full flex-1 overflow-hidden">
      <ScrollableDataTable
        columns={columns}
        data={[...rows]}
        customStyles={dataTableCustomStyles}
        striped
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        paginationDefaultPage={page}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        paginationComponent={pagination}
        onChangePage={onChangePage}
        onChangeRowsPerPage={(next) => onChangeRowsPerPage(next)}
        keyField="id"
        noDataComponent={
          <div className="p-12 text-center">
            <p className="text-sm font-medium text-gray-700">No KB sections match your filters.</p>
            <p className="mt-2 text-sm text-gray-500">
              Create sections in the Word add-in under Create Game Rule → Save to Knowledge Base.
            </p>
          </div>
        }
        highlightOnHover
        responsive
      />
    </div>
  );
};
